import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
import { RoundedTextButton } from "../../prefabs/ui/RoundedTextButton.ts";
import { openTelegramLink } from "$/common/telegram-utils.ts";

interface MiniGameItem {
  name: string;
  iconKey: string;
  onClick: () => void;
}

export class MiniGamesListScene extends BasicScene {
  private background!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private gameItems: Phaser.GameObjects.Container[] = [];
  
  constructor() {
    super("MiniGamesListScene");
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Add background image (brightened version of mini-game background)
    this.background = this.add.image(
      width / 2,
      height / 2,
      this.atlas,
      this.keys.assets.sprites.backgrounds.miniGame.background,
    );
    this.background.setDisplaySize(width, height);
    // Brighten the background
    this.background.setTint(0xFFFFFF);
    this.background.setAlpha(0.8);
    this.background.setDepth(-1);

    // Add title
    this.titleText = this.add.text(
      width / 2,
      50,
      this.keys.strings.sceneTransitions.gamesList,
      {
        fontSize: "48px",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
        fontFamily: "Phantom-Muff",
        fontStyle: "bold",
      },
    );
    this.titleText.setOrigin(0.5, 0);

    // Create mini-game items
    this.createMiniGameItems();

    // Back button
    const backButton = new RoundedTextButton(
      this,
      this.cameras.main.width / 2,
      1200, // Moved down to accommodate the new button
      this.keys.strings.currencyShopScene.exit,
      323,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.danger,
      this.global.TUNER_TWA_CONFIG.palette.danger + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    );

    backButton.on("pointerdown", () => {
      this.transition.toScene(
        "PetHomeScene",
        this.keys.strings.sceneTransitions.backToPet,
      );
    });
  }

  private createMiniGameItems(): void {
    const { width, height } = this.cameras.main;
    
    // Define mini-games
    const miniGames: MiniGameItem[] = [
      {
        name: "Catch Food",
        iconKey: this.keys.assets.sprites.icons.catchFoodIcon,
        onClick: () => {
          if (this.settings.getSetting("isVibrationEnabled")) {
            this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
          }
          this.transition.toScene(
            this.keys.scenes.miniGames.catchFoodGameScene,
            this.keys.strings.sceneTransitions.startingGame,
          );
        }
      },
      {
        name: "Gravity Cats",
        iconKey: this.keys.assets.sprites.icons.gravityCatsIcon, // Using same icon for now
        onClick: () => {
          if (this.settings.getSetting("isVibrationEnabled")) {
            this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
          }
          // Disable music and sounds before redirect
          this.soundManager.stopMusic();
          this.settings.setSetting("isSoundEnabled", false);
          // Redirect to @gravitycats_bot using postEvent web_app_open_tg_link
          openTelegramLink("https://t.me/gravitycats_bot?startapp");
        }
      },
      {
        name: "Flappy Pet",
        iconKey: this.keys.assets.sprites.icons.flappyPatIcon, // Using same icon for now
        onClick: () => {
          if (this.settings.getSetting("isVibrationEnabled")) {
            this.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
          }
          // Disable music and sounds before redirect
          this.soundManager.stopMusic();
          this.settings.setSetting("isSoundEnabled", false);
          // Redirect to @gravitycats_bot using postEvent web_app_open_tg_link
          openTelegramLink("https://t.me/flappy_pet_bot?startapp");
        }
      }
    ];

    // Position items vertically centered
    const startY = this.titleText.y + 250
    const spacing = 350;

    miniGames.forEach((game, index) => {
      const y = startY + index * spacing;
      
      // Create container for each game item
      const container = this.add.container(width / 2, y);
      
      // Add game name text (above the icon)
      const nameText = this.add.text(
        0,
        -180,
        game.name,
        {
          fontSize: "36px",
          color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
          fontFamily: "Phantom-Muff",
          fontStyle: "bold",
        },
      );
      nameText.setOrigin(0.5, 0);
      
      // Add icon/image
      const icon = this.add.image(0, 0, this.atlas, game.iconKey);
      icon.setScale(0.8);
      
      // Make the icon interactive
      icon.setInteractive({ useHandCursor: true });
      
      // Add hover effects
      icon.on("pointerover", () => {
        this.tweens.add({
          targets: icon,
          scaleX: 0.85,
          scaleY: 0.85,
          duration: 200,
          ease: "Power2",
        });
        nameText.setFontSize("40px");
      });
      
      icon.on("pointerout", () => {
        this.tweens.add({
          targets: icon,
          scaleX: 0.8,
          scaleY: 0.8,
          duration: 200,
          ease: "Power2",
        });
        nameText.setFontSize("36px");
      });
      
      // Add click handler
      icon.on("pointerdown", game.onClick);
      
      // Add to container
      container.add([nameText, icon]);
      this.gameItems.push(container);
    });
  }

  destroy(): void {
    this.gameItems.forEach(item => item.destroy());
  }
}